import { useEffect, useState } from 'react';

import { FacilityAccordionList } from '@epics/facilityManagement/components/FacilityList/FacilityAccordionList';
import { listFacilities } from '@epics/facilityManagement/api/listFacilityApiService';
import type { FacilityListItem } from '@epics/facilityManagement/types/facilityListType';
import { FACILITY_LABELS } from '@epics/facilityManagement/constants/facilityLabels';

/** Props for the ViewClientFacilities component. */
interface ViewClientFacilitiesProps {
  clientId: string;
  onAccessDenied?: () => void;
}

/**
 * Displays the "Placówki" tab of the View Client page.
 *
 * Fetches valid (active) facilities for the given client on mount and renders
 * them via `FacilityAccordionList`. Handles loading, error, and empty states
 * internally. The fetch is cancelled on unmount to prevent stale state updates.
 */
const ViewClientFacilities = ({
  clientId,
  onAccessDenied,
}: ViewClientFacilitiesProps) => {
  const [facilities, setFacilities] = useState<FacilityListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetch = async () => {
      setLoading(true);
      setError(null);
      const result = await listFacilities(clientId, {
        facilityIsValid: true,
        equipmentIsValid: true,
        employeeIsValid: true,
      });
      if (cancelled) {
        return;
      }
      if (result.success) {
        setFacilities(result.facilities ?? []);
      } else if (result.forbidden) {
        onAccessDenied?.();
        return;
      } else {
        setError(result.error ?? 'Coś poszło nie tak. Spróbuj ponownie.');
      }
      setLoading(false);
    };

    void fetch();
    return () => {
      cancelled = true;
    };
  }, [clientId, onAccessDenied]);

  return (
    <div className="flex flex-col gap-6 m-[6px]">
      <div className="flex flex-col gap-4 border border-[#DCDDE0] rounded-[8px] p-4">
        <p className="text-[18px] font-medium text-on-surface-variant">
          {FACILITY_LABELS.FACILITY_TITLE}
        </p>
        <FacilityAccordionList
          facilities={facilities}
          loading={loading}
          error={error}
          clientId={clientId}
          employeeIsValid={true}
          {...(onAccessDenied !== undefined && { onAccessDenied })}
        />
      </div>
    </div>
  );
};

export default ViewClientFacilities;
